import type {
    UploadedFileInfo,
    FailedFileInfo,
    FileMetadataInfo,
    DeletedFileInfo,
    DeleteFailedFileInfo,
} from '../types';

// ============================================
// 请求参数类型
// ============================================

type BaseUploadRequest = {
    /** 文件夹路径，用于组织文件存储结构 */
    folder_path?: string;

    /** 文件名前缀，用于标识文件用途 */
    file_prefix?: string;

    /** 是否保留原始文件名 */
    keep_original_name?: boolean;

    /** 文件标签，用于分类和检索 */
    tags?: string[];
};

/**
 * 单文件上传请求参数
 */
export type SingleUploadFileRequest = 
    BaseUploadRequest & ( | {
        /** 文件 */
        file: File;
        /** 文件URL */
        url?: string;
    } | {
        /** 文件 */
        file?: File;
        /** 文件URL */
        url: string;
    });

/**
 * 批量文件上传请求参数
 */
export interface BatchUploadFilesRequest {
    /** 文件列表 */
    files: File[];
    /** 文件夹路径，用于组织文件存储结构 */
    folder_path?: string;
    /** 文件名前缀，用于标识文件用途 */
    file_prefix?: string;
    /** 是否保留原始文件名 */
    keep_original_name?: boolean;
    /** 文件标签，用于分类和检索 */
    tags?: string[];
    /** 允许的文件扩展名，为空则不限制 */
    allowed_extensions?: string[];
    /** 单个文件最大大小（MB），默认10MB */
    max_file_size_mb?: number;
}

/**
 * 获取下载链接请求参数
 */
export interface GetDownloadUrlRequest {
    /** 文件S3路径 */
    s3_key: string;
    /** 链接有效期（分钟），默认60分钟 */
    expire_minutes?: number;
}

/**
 * 删除文件请求参数
 */
export interface DeleteFilesRequest {
    /** S3文件键列表 */
    s3_keys: string[];
}

/**
 * 获取文件元数据请求参数
 */
export interface GetFileMetadataRequest {
    /** 文件S3路径 */
    s3_key: string;
}

/**
 * 检查文件存在请求参数
 */
export interface CheckFileExistsRequest {
    /** 文件S3路径 */
    s3_key: string;
}

// ============================================
// 响应类型
// ============================================

/**
 * 文件上传响应（单文件/批量通用）
 */
export interface GeneralFileUploadResponse {
    /** 上传成功的文件列表 */
    uploaded_files: UploadedFileInfo[];
    /** 上传失败的文件列表 */
    failed_files: FailedFileInfo[];
    /** 总文件数 */
    total_count: number;
    /** 成功上传数 */
    success_count: number;
    /** 失败上传数 */
    failed_count: number;
}

/**
 * 删除文件响应
 */
export interface DeleteFilesResponse {
    /** 删除成功的文件列表 */
    deleted_files: DeletedFileInfo[];
    /** 删除失败的文件列表 */
    failed_files: DeleteFailedFileInfo[];
    /** 总文件数 */
    total_count: number;
    /** 成功删除数 */
    success_count: number;
    /** 失败删除数 */
    failed_count: number;
}

// ============================================
// API 接口定义
// ============================================

export const GeneralFileUploadApi = Symbol.for('GeneralFileUploadApi');

/**
 * 通用文件上传 API 接口
 * @description 提供文件上传、下载、删除和元数据管理的 API 调用
 */
export interface GeneralFileUploadApi {
    /**
     * 单文件上传
     * @param request 上传请求参数
     * @returns 上传响应
     */
    uploadSingleFile(request: SingleUploadFileRequest): Promise<GeneralFileUploadResponse>;

    /**
     * 批量文件上传
     * @param request 批量上传请求参数
     * @returns 上传响应
     */
    uploadMultipleFiles(request: BatchUploadFilesRequest): Promise<GeneralFileUploadResponse>;

    /**
     * 获取文件下载链接
     * @param request 请求参数
     * @returns 签名后的下载URL
     */
    getDownloadUrl(request: GetDownloadUrlRequest): Promise<string>;

    /**
     * 删除文件
     * @param request 删除请求参数
     * @returns 删除响应
     */
    deleteFiles(request: DeleteFilesRequest): Promise<DeleteFilesResponse>;

    /**
     * 检查文件是否存在
     * @param request 请求参数
     * @returns 文件是否存在
     */
    checkFileExists(request: CheckFileExistsRequest): Promise<boolean>;

    /**
     * 获取文件元数据
     * @param request 请求参数
     * @returns 文件元数据信息
     */
    getFileMetadata(request: GetFileMetadataRequest): Promise<FileMetadataInfo>;
}
